import { useState, forwardRef, useRef } from "react";
import {
  FormControl,
  FormErrorMessage,
  Box,
  Input,
  InputProps,
  FormLabel,
  Flex,
  FlexProps,
  Tooltip,
  Icon,
} from "@chakra-ui/react";
import { LayoutGroup, motion, Transition } from "framer-motion";
import { FaInfoCircle } from "react-icons/fa";

interface FieldProps {
  error?: string;
  label?: string;
  containerStyle?: FlexProps;
}

export type KYDInputProps = InputProps &
  FieldProps & {
    min?: number | string;
    max?: number | string;
    tooltip?: string;
  };

const MotionFlex = motion<FlexProps>(Flex);
const MotionLabel = motion(FormLabel);
const MotionBox = motion(Box);

const KYDInput = forwardRef<HTMLInputElement, KYDInputProps>(
  (
    {
      name,
      label,
      error,
      min,
      max,
      type,
      containerStyle,
      variant = "default",
      tooltip,
      ...props
    },
    ref
  ) => {
    const [focus, setFocus] = useState(false);
    const currentValue =
      props.value !== undefined ? props.value : props.defaultValue ?? "";

    const hasValue = currentValue !== "";
    const active = focus || hasValue;

    return (
      <FormControl isInvalid={!!error} w="100%">
        {variant === "default" && (
          <MotionBox
            h="50px"
            boxShadow="0px 0px 10px 0px #0000000D"
            border="1px solid #E6E6E6"
            borderRadius="md"
            p={3}
            py={active ? 1 : 3}
            transition={{ duration: 0.2, ease: "easeOut" }}
            animate={{
              paddingTop: active ? 4 : 12,
              paddingBottom: active ? 4 : 12,
            }}
            {...containerStyle}
          >
            <MotionFlex
              direction={active ? "column" : "row"}
              align={active ? "stretch" : "center"}
              gap={active ? 0 : 2}
              transition={{ duration: 0.2, ease: "easeOut" } as any}
            >
              {label && (
                <MotionLabel
                  htmlFor={name}
                  fontSize={active ? "12px" : "14px"}
                  fontWeight={active ? "400" : "500"}
                  color="#00000080"
                  whiteSpace={active ? "normal" : "nowrap"}
                  mb={0}
                  mr={1}
                  pointerEvents="none"
                  display="flex"
                  alignItems="center"
                  transition={{ duration: 0.2, ease: "easeOut" } as any}
                >
                  {label}
                </MotionLabel>
              )}

              <Box flex="1" display="flex" alignItems="center">
                <Input
                  ref={ref}
                  id={name}
                  name={name}
                  type={type}
                  variant="unstyled"
                  fontFamily="Helvetica"
                  fontSize="14px"
                  _placeholder={{ color: "#00000080" }}
                  _focus={{ outline: "none", boxShadow: "none" }}
                  onFocus={() => setFocus(true)}
                  onChange={props.onChange}
                  {...props}
                  onBlur={(e) => {
                    setFocus(false);
                    props.onBlur?.(e);
                  }}
                />
              </Box>
            </MotionFlex>
          </MotionBox>
        )}

        {variant === "labeled" && (
          <Box display="flex" flexDirection="column" gap={2}>
            <Box>
              {label && (
                <FormLabel
                  htmlFor={name}
                  fontSize={"16px"}
                  fontWeight={"500"}
                  fontFamily={"Work Sans"}
                  lineHeight={"24px"}
                  color="#1A202C"
                  pointerEvents="none"
                  display="inline"
                  margin={0}
                >
                  {label}
                </FormLabel>
              )}
              {tooltip && (
                <Tooltip
                  label={tooltip}
                  fontSize="12px"
                  placement="bottom-start"
                  hasArrow
                  bg="gray.700"
                  color="white"
                  px={2}
                  py={1}
                  borderRadius="md"
                >
                  <span
                    style={{
                      display: "inline-block",
                      alignItems: "center",
                    }}
                  >
                    <Icon
                      as={FaInfoCircle}
                      color="white"
                      background="gray.500"
                      borderRadius="full"
                      marginLeft={2}
                      cursor="pointer"
                      verticalAlign="middle"
                    />
                  </span>
                </Tooltip>
              )}
            </Box>
            <Box
              flex="1"
              display="flex"
              alignItems="center"
              height="54px"
              padding={"14px 17px"}
              border={"1px solid"}
              borderColor={"gray.300"}
              borderRadius="8px"
              bg="white"
              {...containerStyle}
            >
              <Input
                ref={ref}
                id={name}
                name={name}
                type={type}
                variant="unstyled"
                fontFamily="Work Sans"
                fontSize="16px"
                fontWeight={"500"}
                lineHeight={"19.2px"}
                textOverflow={"ellipsis"}
                color={"#1A202C"}
                _placeholder={{ color: "rgba(26, 32, 44, 0.50)" }}
                _focus={{ outline: "none", boxShadow: "none" }}
                onFocus={() => setFocus(true)}
                onChange={props.onChange}
                {...props}
                onBlur={(e) => {
                  setFocus(false);
                  props.onBlur?.(e);
                }}
              />
            </Box>
          </Box>
        )}
        {error && <FormErrorMessage fontSize="12px">{error}</FormErrorMessage>}
      </FormControl>
    );
  }
);

KYDInput.displayName = "KYDInput";

export default KYDInput;
